import { HTMLAttributes, ReactNode } from 'react';
import { css } from '@emotion/react';
import {
  Category as CategoryEnum,
  Sentiment as SentimentEnum,
} from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import Lottie from 'react-lottie';
import { CallIntelTypes } from '@frontend/api-call-intel';
import {
  CallIntelChip,
  PrivateField,
  SchedulingChip,
  transcribingCallLottie,
  useCallIntelDefaultsShallowStore,
} from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { NakedButton, Text, styles as designSystemStyles, useTooltip } from '@frontend/design-system';

type HeaderProps = {
  title: string;
  leftIcon: React.ReactNode;
  rightIcon: React.ReactNode;
  value: string;
  isAnalyzed: boolean;
  isProcessingCall: boolean;
  tooltipContent?: string;
};

type TranscriptProps = {
  children: React.ReactNode;
  maxLines?: number;
};

type SentimentProps = {
  sentiment?: SentimentEnum;
};

type CategoryProps = {
  category?: CategoryEnum;
};

type TasksProps = {
  count?: number;
};

type SchedulingOutcomeProps = {
  call?: CallIntelTypes.CallRecord;
};

type IntelCardProps = {
  children: ReactNode;
  trackingId?: string;
  onClick: () => void;
};

export const IntelCard = ({ children, trackingId, onClick, ...rest }: IntelCardProps) => {
  return (
    <NakedButton css={[containerStyles]} onClick={onClick} trackingId={trackingId} {...rest}>
      {children}
    </NakedButton>
  );
};

export const Header = ({
  title,
  leftIcon,
  rightIcon,
  value,
  isAnalyzed,
  isProcessingCall,
  tooltipContent,
}: HeaderProps) => {
  const { triggerProps, tooltipProps, Tooltip } = useTooltip();

  return (
    <div css={headerStyles}>
      {leftIcon}
      <Text size='medium' weight='bold'>
        {title}
      </Text>
      {!isProcessingCall && (
        <Text size='medium' color='subdued'>
          {value}
        </Text>
      )}
      {!isAnalyzed && !isProcessingCall && (
        <span {...triggerProps}>
          {rightIcon}
          {tooltipContent && <Tooltip {...tooltipProps}>{tooltipContent}</Tooltip>}
        </span>
      )}
    </div>
  );
};

export const Attributes = ({ children, isPrivate }: HTMLAttributes<HTMLDivElement> & { isPrivate?: boolean }) => {
  if (isPrivate) return <PrivateField />;
  return <div css={attributesStyles}>{children}</div>;
};

export const Summary = ({ children, maxLines = 3 }: TranscriptProps) => (
  <Text size='medium' textAlign='left' css={designSystemStyles.multilineTruncate(maxLines)}>
    {children}
  </Text>
);

// Renders sentiment as a chip
export const Sentiment = ({ sentiment }: SentimentProps) => {
  const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');

  if (!sentiment) return null;

  return (
    <CallIntelChip
      as='chip'
      chipVariants={chipVariants}
      dataKey={sentiment}
      label={dataLabels.sentiments?.[sentiment] || sentiment}
      key={sentiment}
    />
  );
};

// Renders category as a chip
export const Category = ({ category }: CategoryProps) => {
  const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');

  if (!category) return null;

  return (
    <CallIntelChip
      as='category'
      chipVariants={chipVariants}
      dataKey={category}
      label={dataLabels.categories?.[category] || category}
      key={category}
    />
  );
};

// Renders task count
export const Tasks = ({ count = 0 }: TasksProps) => {
  const { t } = useTranslation('callIntelInsights');

  if (count === 0) return null;

  return (
    <Text size='medium'>
      {/* This conditional statement is there because pluralization is not working for translations */}
      {count > 1 ? t('{{ taskCount }} Tasks', { taskCount: count }) : t('{{ taskCount }} Task', { taskCount: count })}
    </Text>
  );
};

// Renders scheduling outcome as a chip
export const SchedulingOutcome = ({ call }: SchedulingOutcomeProps) => {
  const schedulingOutcomeV2 = call?.schedulingOutcomeV2;

  if (schedulingOutcomeV2) {
    if (
      [
        CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_NOT_APPLICABLE,
        CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNKNOWN,
      ].includes(schedulingOutcomeV2)
    ) {
      return null;
    }
    return <SchedulingChip call={call} schedulingOutcome={schedulingOutcomeV2} trackingContext='patient-profile' />;
  }

  return null;
};

// Shown when call not analyzed
const NotAnalyzed = () => {
  const { t } = useTranslation('callIntelInsights');
  return (
    <Text size='small' textAlign='left'>
      {t('Last call was not analyzed. Review the call recording.')}
    </Text>
  );
};

// Shown when no data available
const EmptyState = () => {
  const { t } = useTranslation('callIntelInsights');
  return (
    <Text size='small' color='subdued' textAlign='left'>
      {t("No data to display. You'll see patient insights here once Call Intelligence analyzes their calls.")}
    </Text>
  );
};

// Shown when call is still processing
const ProcessingCall = () => {
  const { t } = useTranslation('callIntelInsights');

  return (
    <div css={processingCall}>
      <Lottie
        isClickToPauseDisabled
        options={{
          animationData: transcribingCallLottie,
          loop: true,
          rendererSettings: {
            preserveAspectRatio: 'xMidYMid slice',
          },
        }}
        style={{
          width: '132px',
        }}
      />
      <Text size='small' color='subdued' className='text-row'>
        {t('Processing phone call....')}
      </Text>
      <Text size='small' color='subdued'>
        {t('This may take a few minutes.')}
      </Text>
    </div>
  );
};

IntelCard.Header = Header;
IntelCard.Attributes = Attributes;
IntelCard.Summary = Summary;
IntelCard.Sentiment = Sentiment;
IntelCard.Category = Category;
IntelCard.Tasks = Tasks;
IntelCard.SchedulingOutcome = SchedulingOutcome;
IntelCard.NotAnalyzed = NotAnalyzed;
IntelCard.EmptyState = EmptyState;
IntelCard.ProcessingCall = ProcessingCall;

export default IntelCard;

const containerStyles = css`
  border: 1px solid ${theme.colors.neutral20};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing(1)};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(1)};
  background-color: ${theme.colors.white};
  width: 100%;

  &:hover {
    background-color: ${theme.colors.neutral10};
  }
`;

const headerStyles = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
`;

const attributesStyles = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
  flex-wrap: wrap;
`;

const processingCall = css`
  display: flex;
  flex-direction: column;
  align-items: center;

  .text-row {
    margin-top: ${theme.spacing(-2)};
  }
`;
